/**
 * Alta access token validation.
 *
 * Asks Helium whether the shared Alta access token is still good, matching how
 * Platinum checks it: a server call to
 * `POST /auth/accessTokens/{token}/validate`, not a local decode of the token's
 * expiry claim. Platinum reaches the same endpoint through
 * `validateAccessToken` in `app/heliumApi.js`, consumed by
 * `useVerifyAuthentication` in `app/utils/customHooks/useVerifyAuthentication.tsx`.
 *
 * A bare axios call is used rather than the shared `apiClient`, because
 * `apiClient` is pinned to this service's own Spring Boot backend via
 * `VITE_API_BASE_URL`; Helium is a different origin.
 */
import axios from 'axios';
import { getHeliumBaseUrl } from '../utils/altaEnv';

/** Distinguishes "Helium rejected the token" from "Helium could not be reached". */
export const TOKEN_STATUS = {
  VALID: 'valid',
  INVALID: 'invalid',
  /** Network failure, CORS rejection, timeout, or a 5xx from Helium. */
  UNKNOWN: 'unknown',
};

// Short relative to apiClient's 30s. This check gates first paint, so a hung
// Helium should fall through to UNKNOWN quickly rather than hold a blank screen.
const VALIDATE_TIMEOUT_MS = 10000;

/**
 * Validates an Alta access token.
 *
 * Never throws — the caller decides what to do with each status, and a thrown
 * error here would be indistinguishable from an invalid token.
 *
 * @param {string} token The Alta access token.
 * @param {string} opEnv OP_ENV from `getOpEnv()`.
 * @param {AbortSignal} [signal] Cancels the request on unmount.
 * @returns {Promise<{status: string, error?: unknown}>}
 */
export const validateAccessToken = async (token, opEnv, signal) => {
  const baseUrl = getHeliumBaseUrl(opEnv);
  if (!baseUrl || !token) return { status: TOKEN_STATUS.UNKNOWN };

  // The token is a path segment, so it must be escaped even though Alta tokens
  // are currently URL-safe.
  const url = `${baseUrl}/auth/accessTokens/${encodeURIComponent(token)}/validate`;

  try {
    const response = await axios.post(url, null, {
      signal,
      timeout: VALIDATE_TIMEOUT_MS,
      // Helium reads the token from the path. No cookies are sent: this is a
      // cross-origin request, and credentialed cross-origin requests require
      // Helium to echo an exact origin in Access-Control-Allow-Origin.
      withCredentials: false,
    });

    // Helium wraps payloads in a `data` envelope, so the flag is at
    // `body.data.isValid`. Platinum unwraps the same shape with
    // `select: (data) => data.json.data` and then reads `.isValid`.
    const isValid = response?.data?.data?.isValid;

    // A missing flag is treated as unknown rather than invalid: a 2xx whose
    // body does not match the contract means the response was not understood,
    // and signing the user out on that basis would be wrong.
    if (typeof isValid !== 'boolean') return { status: TOKEN_STATUS.UNKNOWN };

    return { status: isValid ? TOKEN_STATUS.VALID : TOKEN_STATUS.INVALID };
  } catch (error) {
    if (axios.isCancel(error) || error?.name === 'CanceledError') {
      return { status: TOKEN_STATUS.UNKNOWN };
    }

    const status = error?.response?.status;

    // Helium explicitly refused the token. 404 counts: an unknown token has no
    // record to validate against.
    if (status === 401 || status === 403 || status === 404) {
      return { status: TOKEN_STATUS.INVALID, error };
    }

    // Everything else — no response at all (network down, CORS preflight
    // failure), or a 5xx — is Helium's problem, not the token's.
    return { status: TOKEN_STATUS.UNKNOWN, error };
  }
};

export default { validateAccessToken, TOKEN_STATUS };
